import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Button, Tag, Input, Select, Dropdown, message, Modal
} from 'antd';
import {
  PlusOutlined, SearchOutlined, MoreOutlined, EditOutlined,
  CopyOutlined, DeleteOutlined, BarChartOutlined, LinkOutlined,
  EyeOutlined
} from '@ant-design/icons';
import moment from 'moment';
import SurveyService from '../../services/SurveyService';
import ROUTES from '../../constants/routes';
import { PageContainer, StatsRow, StatCard, StyledTable, PaginationText } from './styled';
import useResponsive from '../../hooks/useResponsive';
import MobileCardList from '../../components/Responsive/MobileCardList';
import ResponsiveFilterBar from '../../components/Responsive/ResponsiveFilterBar';

const STATUS_COLORS = {
  draft: 'default',
  active: 'green',
  closed: 'orange',
  archived: 'red'
};

const SurveysPage = observer(() => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState();

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    const result = await SurveyService.listSurveys({
      page: pagination.current,
      limit: pagination.pageSize,
      search,
      status: statusFilter
    });
    if (result.success) {
      setSurveys(result.data.surveys || []);
      setPagination((p) => ({ ...p, total: result.data.pagination?.total || 0 }));
    } else {
      message.error(result.error);
    }
    setLoading(false);
  }, [pagination.current, pagination.pageSize, search, statusFilter]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const handleDelete = (survey) => {
    Modal.confirm({
      title: `Delete survey "${survey.title}"?`,
      content: 'If responses exist, the survey will be archived instead.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        const result = await SurveyService.deleteSurvey(survey._id);
        if (result.success) {
          message.success('Survey deleted');
          loadSurveys();
        } else {
          message.error(result.error);
        }
      }
    });
  };

  const handleDuplicate = async (survey) => {
    const result = await SurveyService.duplicateSurvey(survey._id);
    if (result.success) {
      message.success('Survey duplicated');
      loadSurveys();
    } else {
      message.error(result.error);
    }
  };

  const handleCopyLink = (survey) => {
    const url = `${window.location.origin}/s/${survey.slug}`;
    navigator.clipboard.writeText(url);
    message.success('Public survey link copied to clipboard');
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title, survey) => (
        <div>
          <strong>{title}</strong>
          {survey.description && (
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{survey.description}</div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => <Tag color={STATUS_COLORS[status]}>{status?.toUpperCase()}</Tag>
    },
    {
      title: 'Questions',
      dataIndex: 'questions',
      key: 'questions',
      width: 110,
      render: (questions) => (questions || []).length
    },
    {
      title: 'Responses',
      dataIndex: 'completedCount',
      key: 'completedCount',
      width: 110,
      render: (count) => count || 0
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date) => date ? moment(date).format('MMM D, YYYY') : '—'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_, survey) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => navigate(`/admin/surveys/${survey._id}/edit`)
              },
              {
                key: 'responses',
                icon: <BarChartOutlined />,
                label: 'View Responses',
                onClick: () => navigate(`/admin/surveys/${survey._id}/responses`)
              },
              {
                key: 'preview',
                icon: <EyeOutlined />,
                label: 'Preview',
                onClick: () => window.open(`/s/${survey.slug}`, '_blank')
              },
              {
                key: 'link',
                icon: <LinkOutlined />,
                label: 'Copy Public Link',
                onClick: () => handleCopyLink(survey)
              },
              {
                key: 'duplicate',
                icon: <CopyOutlined />,
                label: 'Duplicate',
                onClick: () => handleDuplicate(survey)
              },
              { type: 'divider' },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: () => handleDelete(survey)
              }
            ]
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      )
    }
  ];

  // Quick stats
  const totalSurveys = surveys.length;
  const activeSurveys = surveys.filter((s) => s.status === 'active').length;
  const totalResponses = surveys.reduce((sum, s) => sum + (s.completedCount || 0), 0);

  return (
    <PageContainer>
      <StatsRow>
        <StatCard>
          <div className="label">Total Surveys</div>
          <div className="value">{pagination.total || totalSurveys}</div>
        </StatCard>
        <StatCard>
          <div className="label">Active</div>
          <div className="value">{activeSurveys}</div>
        </StatCard>
        <StatCard>
          <div className="label">Total Responses (page)</div>
          <div className="value">{totalResponses}</div>
        </StatCard>
      </StatsRow>

      <ResponsiveFilterBar
        activeFilterCount={[statusFilter].filter(Boolean).length}
        search={(
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search surveys..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280, height: 40 }}
            allowClear
          />
        )}
        filters={[
          <Select
            key="status"
            placeholder="Filter by status"
            style={{ width: 180, height: 40 }}
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'Draft', value: 'draft' },
              { label: 'Active', value: 'active' },
              { label: 'Closed', value: 'closed' },
              { label: 'Archived', value: 'archived' }
            ]}
          />
        ]}
        primaryAction={{
          label: 'New Survey',
          icon: <PlusOutlined />,
          onClick: () => navigate(ROUTES.ADMIN_SURVEY_BUILDER_NEW),
          style: { background: '#E31B23', borderColor: '#E31B23' }
        }}
      />

      {isMobile ? (
        <MobileCardList
          records={surveys}
          rowKey="_id"
          loading={loading}
          onRowClick={(s) => navigate(`/admin/surveys/${s._id}/edit`)}
          emptyText="No surveys yet"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
          }}
          renderCard={(s) => (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{s.title}</div>
                  {s.description && (
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{s.description}</div>
                  )}
                </div>
                <Tag color={STATUS_COLORS[s.status]} style={{ flexShrink: 0 }}>{s.status?.toUpperCase()}</Tag>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                marginTop: 12,
                fontSize: 12
              }}>
                <div><div style={{ color: '#8c8c8c' }}>Questions</div><div style={{ fontWeight: 600 }}>{(s.questions || []).length}</div></div>
                <div><div style={{ color: '#8c8c8c' }}>Responses</div><div style={{ fontWeight: 600 }}>{s.completedCount || 0}</div></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {s.createdAt ? moment(s.createdAt).format('MMM D, YYYY') : ''}
                </span>
                <Dropdown
                  menu={{
                    items: [
                      { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => navigate(`/admin/surveys/${s._id}/edit`) },
                      { key: 'responses', icon: <BarChartOutlined />, label: 'View Responses', onClick: () => navigate(`/admin/surveys/${s._id}/responses`) },
                      { key: 'preview', icon: <EyeOutlined />, label: 'Preview', onClick: () => window.open(`/s/${s.slug}`, '_blank') },
                      { key: 'link', icon: <LinkOutlined />, label: 'Copy Public Link', onClick: () => handleCopyLink(s) },
                      { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate', onClick: () => handleDuplicate(s) },
                      { type: 'divider' },
                      { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: () => handleDelete(s) }
                    ]
                  }}
                  trigger={['click']}
                >
                  <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                </Dropdown>
              </div>
            </div>
          )}
        />
      ) : (
        <StyledTable
          columns={columns}
          dataSource={surveys}
          rowKey="_id"
          loading={loading}
          size="middle"
          scroll={{ x: 800 }}
          onRow={(record) => ({
            onClick: () => navigate(`/admin/surveys/${record._id}/edit`),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => (
              <PaginationText>Showing {range[0]}-{range[1]} of {total} surveys</PaginationText>
            ),
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
          }}
        />
      )}
    </PageContainer>
  );
});

export default SurveysPage;
